import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Dialog, { Clip, Next } from '../molecules/Dialog';
import Grid from '../atoms/Grid';
import Image from '../atoms/Image';
import type { Concept } from '../../data/Concepts';
import Text from '../atoms/Text';
import TextBlock from '../molecules/TextBlock';
import AutofitText from '../molecules/AutofitText';
import Centered from '../atoms/Centered';
import { getImageByKey } from '../../assets/Assets';

const CaptionBefore = styled.div`
	background-color: ${(props) => props.theme.colors.darkRed};
	border-radius: 9999px;
	color: ${(props) => props.theme.colors.white};
	font: ${(props) => props.theme.font.default};
	font-size: 7vmin;
	padding-left: 0.5em;
	padding-right: 0.5em;
	text-shadow: ${(props) => props.theme.shadow.caption};
`;

const CaptionAfter = styled.div`
	background-color: ${(props) => props.theme.colors.lightGreen};
	border-radius: 9999px;
	color: ${(props) => props.theme.colors.white};
	font: ${(props) => props.theme.font.default};
	font-size: 7vmin;
	padding-left: 0.5em;
	padding-right: 0.5em;
	text-shadow: ${(props) => props.theme.shadow.caption};
`;

interface PictureProps {
	grayscale?: boolean;
}

const Picture = styled.div<PictureProps>`
	background-color: ${(props) => props.theme.colors.veryLightBlue};
	border-radius: 10vmin;
	bottom: 0;
	left: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	top: 3.5vmin;
	filter: ${(props) => (props.grayscale ?? false ? 'grayscale(1)' : 'unset')};
`;

export interface Props {
	concept: Concept;
	onNext: (concept: Concept) => void;
}

const EffectDialog: FunctionComponent<Props> = (props: Props) => {
	const { concept, onNext } = props;
	const { t } = useTranslation();

	const imageBefore = useMemo(
		() => getImageByKey(concept.images.before),
		[concept]
	);
	const imageAfter = useMemo(
		() => getImageByKey(concept.images.after),
		[concept]
	);

	return (
		<Dialog>
			<Clip>
				<Grid
					columns={2}
					rows={1}
					position="absolute"
					left="2%"
					right="2%"
					top="4%"
					bottom="20%"
				>
					<>
						<Picture grayscale>
							<Image src={imageBefore} width="100%" height="100%" />
						</Picture>
						<Centered position="absolute" top="0" left="0" right="0">
							<CaptionBefore>
								<Trans>concept_effect.before</Trans>
							</CaptionBefore>
						</Centered>
					</>
					<>
						<Picture>
							<Image src={imageAfter} width="100%" height="100%" />
						</Picture>
						<Centered position="absolute" top="0" left="0" right="0">
							<CaptionAfter>
								<Trans>concept_effect.after</Trans>
							</CaptionAfter>
						</Centered>
					</>
				</Grid>
				<TextBlock
					height="20%"
					position="absolute"
					left="25%"
					right="25%"
					bottom="5%"
				>
					<AutofitText>
						<Text content={t(concept.texts.resultText)} />
					</AutofitText>
				</TextBlock>
			</Clip>
			<Next onClick={() => onNext(concept)}>
				<Trans>concept_effect.next</Trans>
			</Next>
		</Dialog>
	);
};

export default EffectDialog;
